import type { FetchResult } from "@apollo/client";
import { Mutation } from "@apollo/client/react/components";
import transformOnAssetDiscussionCommentWithRepliesCountFields from "components/discussions/apollo/transformOnAssetDiscussionCommentWithRepliesCountFields";
import useViewerCountry from "lib/hooks/useViewerCountry";
import { useAmplitude } from "../../../lib/amplitude/Amplitude";
import ReplyToAssetDiscussionComment from "../../../lib/graphql/mutations/discussions/ReplyToAssetDiscussionComment";
import type {
  IReplyToAssetDiscussionCommentMutation,
  IReplyToAssetDiscussionCommentMutationVariables,
} from "../../../lib/graphql/mutations/discussions/__generated__/ReplyToAssetDiscussionComment.generated";
import type { IAssetType } from "../../../types/graphqlTypes";
import transformOnDiscussionCommentsRootQuery from "../apollo/transformOnDiscussionCommentsRootQuery";
import transformOnSingleCommentRootQuery from "../apollo/transformOnSingleCommentRootQuery";

interface IReplyToAssetDiscussionCommentMutationArgs {
  assetId: string;
  assetType: IAssetType;
  replyToCommentId: string;
  replyToCommentShortId: string;
  bodyAsPlainText: string;
}

interface IReplyToAssetDiscussionCommentMutationProps {
  children: (
    mutate: (
      args: IReplyToAssetDiscussionCommentMutationArgs,
    ) => Promise<FetchResult<IReplyToAssetDiscussionCommentMutation> | void>,
  ) => any;
}

export default function ReplyToAssetDiscussionCommentMutation({
  children,
}: IReplyToAssetDiscussionCommentMutationProps) {
  const amplitude = useAmplitude();
  const country = useViewerCountry();

  return (
    <Mutation<IReplyToAssetDiscussionCommentMutation, IReplyToAssetDiscussionCommentMutationVariables>
      mutation={ReplyToAssetDiscussionComment}
    >
      {(replyToAssetDiscussionComment) => {
        return children(({ assetId, assetType, replyToCommentId, replyToCommentShortId, bodyAsPlainText }) => {
          amplitude.logEvent("Replied to a comment", {
            assetId,
            assetType,
            country,
            contentCharacterCount: bodyAsPlainText.length,
          });

          return replyToAssetDiscussionComment({
            variables: {
              commentId: replyToCommentId,
              bodyAsPlainText,
            },
            update: (dataProxy, queryResult) => {
              if (!queryResult.data || !queryResult.data.replyToAssetDiscussionComment) {
                return;
              }

              transformOnAssetDiscussionCommentWithRepliesCountFields(dataProxy, replyToCommentId, (fragmentData) => {
                return {
                  ...fragmentData,
                  replies: {
                    ...fragmentData.replies,
                    totalCount: fragmentData.replies.totalCount + 1,
                  },
                };
              });

              transformOnDiscussionCommentsRootQuery(dataProxy, assetId, (rootQueryData) => {
                return {
                  ...rootQueryData,
                  nodes: rootQueryData.nodes.map((rootCommentNode) => {
                    if (!queryResult || !queryResult.data || !queryResult.data.replyToAssetDiscussionComment) {
                      return rootCommentNode;
                    }

                    if (rootCommentNode._id !== replyToCommentId) {
                      return rootCommentNode;
                    }

                    const nodesAfter = rootCommentNode.replies.nodes.concat(
                      queryResult.data.replyToAssetDiscussionComment,
                    );

                    return {
                      ...rootCommentNode,
                      replies: {
                        ...rootCommentNode.replies,
                        nodes: nodesAfter,
                        totalCount:
                          nodesAfter.length > rootCommentNode.replies.nodes.length
                            ? Math.max(rootCommentNode.replies.totalCount + 1, 0)
                            : rootCommentNode.replies.totalCount,
                      },
                    };
                  }),
                };
              });

              transformOnSingleCommentRootQuery(dataProxy, replyToCommentShortId, (rootQueryData) => {
                if (!rootQueryData) {
                  return rootQueryData;
                }

                if (!queryResult || !queryResult.data || !queryResult.data.replyToAssetDiscussionComment) {
                  return rootQueryData;
                }

                const nodesAfter = rootQueryData.replies.nodes.concat(queryResult.data.replyToAssetDiscussionComment);

                return {
                  ...rootQueryData,
                  replies: {
                    ...rootQueryData.replies,
                    nodes: nodesAfter,
                    totalCount:
                      nodesAfter.length > rootQueryData.replies.nodes.length
                        ? Math.max(rootQueryData.replies.totalCount + 1, 0)
                        : rootQueryData.replies.totalCount,
                  },
                };
              });
            },
          });
        });
      }}
    </Mutation>
  );
}
